import React, { useMemo, useEffect } from "react";
import { ListItemText, Box, Typography, List, useTheme } from "@mui/material";
import { NavLink } from "react-router-dom";
import "../assets/style.css";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from '../state/store';
import { setPage } from "../state/appSlice";
import { useTypedSelector } from "../hooks/useTypedSelector";
import { PackageTypeUnassigned } from "../utils/constants";
import { IPackage } from "../models/configuration/plugin/IConfiguration";

const SideBarASO: React.FC = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const appState = useSelector((state: RootState) => state.appSlice);

    // ASO packages source
    const asoPackages = useTypedSelector((state) => state.configASOPluginData.configData?.Packages) as IPackage[] | undefined;
    
    // Build links from ASO package groups (no redirects or listeners)
    const sidebarLinks = useMemo(() => {
        const pkgs = (asoPackages ?? []).slice();
        pkgs.sort((a, b) => Number(a.Order ?? 0) - Number(b.Order ?? 0));
        const groups: Array<{ name: string; path: string }> = [];
        for (let i = 0; i < pkgs.length; i++) {
            const g = pkgs[i]?.Group;
            if (!g) continue;
            if (g.toLowerCase() === PackageTypeUnassigned.toLowerCase()) continue;
            if (!groups.some(x => x.name === g)) groups.push({ name: g, path: '/Order' });
        }
        return groups;
    }, [asoPackages]);

    // Auto-select the default group based on global defaultPackageId
    useEffect(() => {
        const defaultId = (appState.defaultPackageId ?? '').toString();
        if (!asoPackages || asoPackages.length === 0) return;

        const pageUnset = !appState.page || appState.page === '/';
        const pageUnknown = !sidebarLinks.some(x => x.name === appState.page);

        if (defaultId) {
            if (!pageUnset && !pageUnknown) return; // Don't override existing page selection

            const pkg = asoPackages.find(p => (p.Id ?? '').toString().toLowerCase() === defaultId.toLowerCase());
            const group = pkg?.Group;
            if (!group) return;
            if (group.toLowerCase() === PackageTypeUnassigned.toLowerCase()) return;

            // If default group exists in links, use it; otherwise fall back to first
            const hasDefaultGroup = sidebarLinks.some(x => x.name === group);
            const targetGroup = hasDefaultGroup ? group : (sidebarLinks[0]?.name ?? '');
            if (targetGroup) dispatch(setPage(targetGroup));
        } else {
            // No default id: pick first group by default when page is unset/unknown
            if (pageUnset || pageUnknown) {
                const firstGroup = sidebarLinks[0]?.name ?? '';
                if (firstGroup) dispatch(setPage(firstGroup));
            }
        }
    }, [appState.defaultPackageId, appState.page, asoPackages, sidebarLinks, dispatch]);

    // Styles copied from SideBar for visual consistency
    const navLinkStyle = ({ isActive }: { isActive: boolean }) => ({
        textDecoration: "none",
        color: theme.palette.primary.contrastText,
        fontWeight: isActive ? 600 : 400,
        fontSize: "0.95rem",
        display: "flex",
        alignItems: "center",
        borderRadius: 8,
        background: isActive ? theme.palette.primary.main : "none",
        transition: "background 0.2s",
        padding: "8px 18px",
        marginBottom: 4,
        "&:hover": {
            background: isActive ? theme.palette.primary.main : theme.palette.action.hover,
            color: theme.palette.primary.contrastText,
        }
    });

    return (
        <Box
            sx={{
                background: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Only the Home-style list of ASO package groups */}
            <Box sx={{ overflowY: 'auto', flexGrow: 1, minHeight: 0 }}>
                <List component="nav" sx={{ px: 1, pt: 2 }}>
                    {sidebarLinks.map((link) => (
                        <NavLink
                            key={link.name}
                            to={link.path}
                            style={() => navLinkStyle({ isActive: appState.page === link.name })}
                            onClick={() => {
                                dispatch(setPage(link.name));
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Typography sx={{ fontWeight: 500, letterSpacing: 0.5 }}>
                                        {link.name}
                                    </Typography>
                                }
                            />
                        </NavLink>
                    ))}
                </List>
            </Box>
        </Box>
    );
};

export default SideBarASO;
